import { gql, useQuery } from "@apollo/client";
import {
  FindAllLocationsAdminQueryQuery,
  QueryFindAllLocationsAdminArgs,
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllLocationsAdminQuery = gql`
  query FindAllLocationsAdminQuery(
    $page: Int!
    $pageSize: Int!
    $countryFilter: String
    $statusFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllLocationsAdmin(
      page: $page
      pageSize: $pageSize
      countryFilter: $countryFilter
      statusFilter: $statusFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        district
        city
        streetName
        country
        postalCode
        landmark
        createdAt
        latitude
        longitude
        isArchive
        customerCount
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllLocationsAdmin {
  data: FindAllLocationsAdminQueryQuery['findAllLocationsAdmin']['data'];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreLocations: boolean;
}

interface Props {
  variables: QueryFindAllLocationsAdminArgs;
  children: (data: WithFindAllLocationsAdmin) => React.ReactElement | null;
}

export const FindAllLocationsAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllLocationsAdminQueryQuery,
    QueryFindAllLocationsAdminArgs
  >(findAllLocationsAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllLocationsAdminData = data?.findAllLocationsAdmin?.data || [];
  const page = data?.findAllLocationsAdmin?.page || variables.page;
  const pageSize = data?.findAllLocationsAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllLocationsAdmin?.total || 0;
  const totalPages = data?.findAllLocationsAdmin?.totalPages || 0;
  const hasMoreLocations = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreLocations) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllLocationsAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllLocationsAdmin: {
            ...fetchMoreResult?.findAllLocationsAdmin,
            data: [
              ...(prev?.findAllLocationsAdmin?.data || []),
              ...(fetchMoreResult?.findAllLocationsAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreLocations]);

  return children({
    data: findAllLocationsAdminData as any,
    loading,
    loadMore,
    hasMoreLocations,
    page,
    pageSize,
    total,
    totalPages,
  });
};
